import React, { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Outlet, Navigate, useLocation } from "react-router-dom";
import Loader from "../UI/Loader";

// helper
import { isAuth } from "../../Library/Utils/Auth";
import { flattenObject } from "Library/Utils";
import {ls_vars, routes, PLATFORM_CODE} from "Library/Constants";
import { getLocalStorage, setLocalStorage } from "Network/ApiService";
import {
  getCurrentUserData,
  getFeatureFlags,
  getUserPreferences,
} from "Network/Core/AuthModule/auth";
import toast from "react-hot-toast";
import { getProjectSettingList } from "Network/Core/Booking/booking";
import { getProjectFieldsDetails } from "Network/Core/Dashboard/dashboard";
import { useDispatch } from "react-redux";
import { setFlags } from "Store/Actions/AuthModule/authActionCreator";
import { getDataSiteList } from "Network/Core/SiteManagement/siteDetails";
import { User, UserProject } from "Types/User";
import { setLanguage } from "Store/Actions/langaugeActionCreator";
import { GetCountryUomListParams } from "Network/Core/CountryUom/types";
import { getCountryUomList } from "Network/Core/CountryUom/CountryUom";
import countriesOptionsData from "StaticData/ProfileModule/countries.json";
import SystemBanner from "Components/UI/SystemBanner";

const PrivateLayout = ({ children }: any) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);

  const countries: any = countriesOptionsData || [];

  const user: User = getLocalStorage(ls_vars.USER_DATA);
  const user_projects: UserProject[] = getLocalStorage(ls_vars.USER_PROJECTS);
  const user_preferences = getLocalStorage(ls_vars.USER_PREFERENCES);
  const project = getLocalStorage(ls_vars.PROJECT_REF);
  const region = getLocalStorage(ls_vars.REGION_ID);

  const checkAndUpdateUserData = async () => {
    // COLLECT ALL BASIC DATA THAT WE USE ON EVERY PAGE
    if (!loading) {
      setLoading(true);
      try {
        let userRef = user?.ref;
        let projectRef = project;
        let regionId = region;
        let userProjectsRes = user_projects;

        // USER AND BASIC DATA
        const userRes = await getCurrentUserData();
        if (userRes?.data) {
          userRef = userRes?.data?.ref;
          setLocalStorage(ls_vars.USER_DATA, JSON.stringify(userRes.data));
          setLocalStorage(
            ls_vars.USER_IS_ADMIN,
            userRes.data.isGlobalAdminUser
          );
          if (!projectRef || !regionId) {
            userProjectsRes = userRes?.data?.projects;
            const firstProjectRef = userRes?.data.projects?.[0]?.ref;
            const firstProjectRegion = userRes?.data.projects?.[0]?.region?.id;
            const firstProjectRole = userRes?.data.projects?.[0]?.role;
            if (!projectRef) {
              projectRef = firstProjectRef;
              setLocalStorage(ls_vars.PROJECT_REF, firstProjectRef);
            }
            if (!regionId) {
              regionId = firstProjectRegion;
              setLocalStorage(ls_vars.REGION_ID, firstProjectRegion);
            }
            setLocalStorage(
              ls_vars.PROJECT_ROLE,
              JSON.stringify(firstProjectRole)
            );
          }
        }
        // PROJECTS RELATED DATA
        const currentUserFullProjectsList = userRes?.data?.projects || userProjectsRes;
        if (currentUserFullProjectsList) {
          setLocalStorage(
            ls_vars.USER_PROJECTS,
            JSON.stringify(currentUserFullProjectsList)
          );
          const selectedValue = currentUserFullProjectsList.find(
            (item: any) => item.ref === projectRef
          );
          if (selectedValue) {
            // Get current project data from localStorage
            const currentProjectDataStr = getLocalStorage(ls_vars.PROJECT_DATA);
            let currentProjectData: Record<string, any>;
            try {
              currentProjectData = currentProjectDataStr || {};
            } catch (e) {
              currentProjectData = {};
            }

            // Check if this is the same project or a different one
            const isSameProject = currentProjectData?.ref === selectedValue?.ref;

            if (isSameProject && currentProjectData && Object.keys(currentProjectData).length > 0) {
              // Same project - use complex logic to update only changed fields
              const flattenedCurrent = flattenObject(currentProjectData);
              const flattenedNew = flattenObject(selectedValue);

              let hasChanges = false;
              const updatedProjectData = { ...currentProjectData };

              // Compare flattened objects and identify changes
              Object.keys(flattenedNew).forEach((flatKey) => {
                const currentValue = flattenedCurrent[flatKey];
                const newValue = flattenedNew[flatKey];

                const areEqual = Array.isArray(newValue)
                  ? JSON.stringify(newValue) === JSON.stringify(currentValue)
                  : newValue === currentValue;

                if (!areEqual) {
                  const keyParts = flatKey.split(".");
                  let target = updatedProjectData;

                  for (let i = 0; i < keyParts.length - 1; i++) {
                    const part = keyParts[i];
                    if (!target[part]) target[part] = {};
                    target = target[part];
                  }

                  const lastKey = keyParts[keyParts.length - 1];
                  target[lastKey] = newValue;
                  hasChanges = true;
                }
              });

              if (hasChanges) {
                setLocalStorage(
                  ls_vars.PROJECT_DATA,
                  JSON.stringify(updatedProjectData)
                );
              }
              getCountryUomData(countries.find((c: any) => c.value === updatedProjectData?.country), regionId);
            } else {
              // Different project or no existing data - completely replace
              setLocalStorage(
                ls_vars.PROJECT_DATA,
                JSON.stringify(selectedValue)
              );
              getCountryUomData(countries.find((c: any) => c.value === selectedValue?.country), regionId);
            }

            // Update timezone only if it changed
            const currentTimezone = getLocalStorage(ls_vars.PROJECT_TIMEZONE);
            if (currentTimezone !== selectedValue.timezone) {
              setLocalStorage(ls_vars.PROJECT_TIMEZONE, selectedValue.timezone);
            }
          }

          const projectSettingRes = await getProjectSettingList(projectRef);
          if (projectSettingRes?.status !== 200) {
            setLocalStorage(ls_vars.PROJECT_SETTINGS, null);
            throw new Error('Failed to load project settings');
          }

          if (projectSettingRes?.data) {
            setLocalStorage(
              ls_vars.PROJECT_SETTINGS,
              JSON.stringify(projectSettingRes?.data.results)
            );
          }

          const projectFieldsRes = await getProjectFieldsDetails(projectRef);
          if (projectFieldsRes?.status !== 200) {
            setLocalStorage(ls_vars.PROJECT_BOOKING_FIELDS, null);
            throw new Error('Failed to load project fields');
          }

          if (projectFieldsRes?.data) {
            setLocalStorage(
              ls_vars.PROJECT_BOOKING_FIELDS,
              JSON.stringify(projectFieldsRes?.data)
            );
          }
        }

        const siteRes = await getDataSiteList(projectRef);
        if (!siteRes?.data) {
          setLocalStorage(ls_vars.PROJECT_SITE_ID, null);
          throw new Error('Failed to load project sites');
        }

        if (siteRes?.data?.results?.[0].id) {
          setLocalStorage(
            ls_vars.PROJECT_SITE_ID,
            siteRes.data.results?.[0].id
          );
        }
        const featureFlagsRes = await getFeatureFlags();
        if (featureFlagsRes?.status !== 200) {
          throw new Error('Failed to load feature flags');
        }

        if (featureFlagsRes?.data) {
          const flagsObj: Record<string, boolean | Record<string, boolean>> =
            {};
          featureFlagsRes?.data.forEach((flag: any) => {
            let tempFlag: Record<string, boolean> = {};
            tempFlag[flag.region.id] = flag.enabled;
            flagsObj[flag.feature.code] = Object.assign(
              flagsObj[flag.feature.code] || {},
              tempFlag
            );
          });
          dispatch(setFlags(flagsObj));
        }

        // USER PREFERENCES DATA
        const shouldFetchPreferences = !user_preferences || typeof user_preferences !== "object";

        if (shouldFetchPreferences) {
          const res = await getUserPreferences(regionId);
          if (res?.status !== 200) {
            setLocalStorage(ls_vars.USER_PREFERENCES, null);
            throw new Error('Failed to load user preferences');
          }

          if (res?.data) {
            setLocalStorage(
              ls_vars.USER_PREFERENCES,
              JSON.stringify(res?.data)
            );
            dispatch(setLanguage(res?.data?.language?.code));
          }
        }

        setLoading(false);
      } catch (error: any) {
        setLoading(false);
        toast.error('Project settings could not be loaded. Please select a different project.');
        window.location.href = routes.auth.choseProject;
      }
    }
  };

  useEffect(() => {
    checkAndUpdateUserData();
  }, [project]); // Re-run when project changes

  if (loading) return <Loader />;
  return (
    <>
      {/* Regular SystemBanner component */}
      <SystemBanner />
      {children}
    </>
  );
};

const getCountryUomData = async (projectCountry: any, region: number) => {
  try {
    // Get project country based UOM
    const params: GetCountryUomListParams = {
      page: 1,
      pageSize: 1,
      country: projectCountry?.label?.trim()
    };

    const res = await getCountryUomList(params, region);
    const countryUom = res?.data?.results[0] || {};
    setLocalStorage(
      ls_vars.COUNTRY_UOM,
      JSON.stringify(countryUom)
    );
  } catch {}
}

const LightweightLayout = ({ children }: any) => {
  const [loading, setLoading] = useState(false);
  const user: User = getLocalStorage(ls_vars.USER_DATA);

  useEffect(() => {
    if (!user && !loading) {
      setLoading(true);
      getCurrentUserData()
        .then((userRes) => {
          if (userRes?.data) {
            setLocalStorage(ls_vars.USER_DATA, JSON.stringify(userRes.data));
            //  Filter only projects for this platform
            const pjs = (userRes.data.projects || []).filter((p: any) => p.platform.code === PLATFORM_CODE);
            setLocalStorage(ls_vars.USER_PROJECTS, JSON.stringify(pjs));
            setLocalStorage(ls_vars.USER_IS_ADMIN, userRes.data.isGlobalAdminUser);
          }
        })
        .catch(() => {
          // Handle error silently or redirect to login
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [user, loading]);

  if (loading) return <Loader />;
  return children;
};

export default function PrivateRoutes() {
  const isAuthorized = isAuth();
  const location = useLocation();
  const isDataLimited = location.pathname.includes(routes.auth.choseProject);

  if (!isAuthorized) return <Navigate to={routes.auth.login} />;
  if (isAuthorized && isDataLimited) {
    return (
      <LightweightLayout>
        <Outlet />
      </LightweightLayout>
    );
  }
  return (
    <PrivateLayout>
      <Outlet />
    </PrivateLayout>
  );
}
